import { useRef, useState } from 'react';
import {
  Button,
  Card,
  Radio,
  Select,
  Steps,
  Table,
  Upload,
  Typography,
  Space,
  Divider,
} from 'antd';
import { InboxOutlined, DownloadOutlined, UploadOutlined } from '@ant-design/icons';

import { useTranslation } from '@govard/i18n-core/react';

import {
  createImportJob,
  executeImport,
  exportEntities,
  updateImportMapping,
  type ImportPreview,
} from '../api/imports.client.js';

import { ErrorState } from '../../../shared/ui/ErrorState.js';
import { PageHeader } from '../../../shared/ui/PageHeader.js';

const { Dragger } = Upload;
const { Text } = Typography;

const ENTITY_OPTIONS = [
  { value: 'contact', labelKey: 'crm.imports.entity.contacts' },
  { value: 'company', labelKey: 'crm.imports.entity.companies' },
  { value: 'deal', labelKey: 'crm.imports.entity.deals' },
];

// entity field -> i18n key suffix under `crm.imports.field.`
const FIELD_KEYS: Record<string, Record<string, string>> = {
  contact: {
    firstName: 'firstName',
    lastName: 'lastName',
    email: 'email',
    phone: 'phone',
    company: 'company',
    role: 'role',
    tags: 'tags_comma',
    notes: 'notes',
  },
  company: {
    name: 'name',
    domain: 'domain',
    industry: 'industry',
    website: 'website',
    phone: 'phone',
    description: 'description',
    tags: 'tags',
  },
  deal: {
    title: 'title',
    amount: 'amount',
    stage: 'stage',
    currency: 'currency',
    description: 'description',
  },
};

function fieldLabels(entityType: string, t: (key: string) => string): Record<string, string> {
  return Object.fromEntries(
    Object.entries(FIELD_KEYS[entityType] ?? {}).map(([field, key]) => [
      field,
      t(`crm.imports.field.${key}`),
    ]),
  );
}

export default function ImportsPage() {
  const { t } = useTranslation();
  const [entityType, setEntityType] = useState<string>('contact');
  const [file, setFile] = useState<File | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);
  const [jobUpdatedAt, setJobUpdatedAt] = useState<string | null>(null);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // «Повторить» reruns the step that failed (upload / save mapping / execute).
  const retryRef = useRef<(() => Promise<void>) | null>(null);
  const [result, setResult] = useState<{
    created: number;
    skipped: number;
    failed: number;
    errors: Array<{ row: number; message: string }>;
  } | null>(null);

  const handleUpload = async () => {
    if (!file) return;
    retryRef.current = handleUpload;
    setLoading(true);
    setError(null);
    try {
      const res = await createImportJob(entityType, file);
      setJobId(res.id);
      setJobUpdatedAt(res.updatedAt);
      setPreview(res.preview);
      setMapping(res.mapping);
      setStep(1);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  const handleMappingChange = (header: string, field: string) => {
    setMapping((prev) => {
      const next = { ...prev };
      if (!field) delete next[header];
      else next[header] = field;
      return next;
    });
  };

  const handleSaveMapping = async () => {
    if (!jobId || !jobUpdatedAt) return;
    retryRef.current = handleSaveMapping;
    setLoading(true);
    setError(null);
    try {
      const updated = await updateImportMapping(jobId, mapping, jobUpdatedAt);
      setJobUpdatedAt(updated.updatedAt);
      setStep(2);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  const handleExecute = async () => {
    if (!jobId || !jobUpdatedAt) return;
    retryRef.current = handleExecute;
    setLoading(true);
    setError(null);
    try {
      // The server executes the mapping stored on the job, so the edited
      // mapping has to be persisted first: running with a stale one imports
      // every row into fields the user did not choose.
      const saved = await updateImportMapping(jobId, mapping, jobUpdatedAt);
      setJobUpdatedAt(saved.updatedAt);
      const res = await executeImport(jobId, saved.updatedAt);
      setResult(res);
      setStep(3);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  const fields = fieldLabels(entityType, t);
  const fieldOptions = Object.entries(fields).map(([value, label]) => ({
    value,
    label: `${label} (${value})`,
  }));

  const previewColumns = preview
    ? preview.headers.map((h) => ({ title: h, dataIndex: h, key: h }))
    : [];
  const previewData = preview
    ? preview.rowsPreview.map((row, idx) => {
        const obj: Record<string, string> = { key: String(idx) };
        row.forEach((val, i) => {
          const h = preview.headers[i];
          if (h) obj[h] = val;
        });
        return obj;
      })
    : [];

  return (
    <div className="animate-fade-in mx-auto w-full p-6" style={{ maxWidth: 1200 }}>
      <PageHeader title={t('crm.imports.title')} subtitle={t('crm.imports.subtitle')} />

      <Steps
        current={step}
        style={{ margin: '24px 0' }}
        items={[
          { title: t('crm.imports.step_upload') },
          { title: t('crm.imports.step_preview') },
          { title: t('crm.imports.step_mapping') },
          { title: t('crm.imports.step_result') },
        ]}
      />

      {error !== null && (
        <div style={{ marginBottom: 16 }}>
          {/* Shared error surface with a working retry instead of a bare Alert. */}
          <ErrorState
            title={t('crm.imports.error_title')}
            error={error}
            onRetry={() => void retryRef.current?.()}
          />
        </div>
      )}

      {step === 0 && (
        <Card>
          <Space orientation="vertical" style={{ width: '100%' }} size={16}>
            <div>
              <Text strong>{t('crm.imports.entity')}</Text>
              <Radio.Group
                value={entityType}
                onChange={(e) => setEntityType(e.target.value)}
                style={{ marginLeft: 12 }}
              >
                {ENTITY_OPTIONS.map((o) => (
                  <Radio key={o.value} value={o.value}>
                    {t(o.labelKey)}
                  </Radio>
                ))}
              </Radio.Group>
            </div>
            <div data-testid="excel-import-dragger">
              <Dragger
                beforeUpload={(f) => {
                  setFile(f);
                  return false;
                }}
                maxCount={1}
                accept=".xlsx,.xls"
                fileList={file ? [{ uid: '-1', name: file.name, status: 'done' } as never] : []}
                onRemove={() => setFile(null)}
              >
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text">{t('crm.imports.upload_hint')}</p>
              </Dragger>
            </div>
            <Button
              type="primary"
              icon={<UploadOutlined />}
              onClick={handleUpload}
              loading={loading}
              disabled={!file}
            >
              {t('crm.imports.upload_and_preview')}
            </Button>
          </Space>
        </Card>
      )}

      {step === 1 && preview && (
        <Card
          title={
            preview.truncated
              ? t('crm.imports.preview.title_truncated', {
                  shown: preview.rowsPreview.length,
                  totalRows: preview.totalRows,
                })
              : t('crm.imports.preview.title', { totalRows: preview.totalRows })
          }
        >
          <Table
            data-testid="excel-import-preview-table"
            columns={previewColumns}
            dataSource={previewData}
            pagination={false}
            size="small"
            bordered
          />
          <div style={{ marginTop: 16 }}>
            <Button type="primary" onClick={() => setStep(2)}>
              {t('crm.imports.next_to_mapping')}
            </Button>
          </div>
        </Card>
      )}

      {step === 2 && preview && (
        <Card data-testid="excel-import-mapping-card" title={t('crm.imports.column_mapping')}>
          <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
            {t('crm.imports.mapping_hint')}
          </Text>
          {preview.headers.map((h) => (
            <div
              key={h}
              style={{ display: 'flex', gap: 12, marginBottom: 8, alignItems: 'center' }}
            >
              <Text style={{ width: 180, textAlign: 'right' }} strong>
                {h}
              </Text>
              <Select
                data-testid={`excel-import-select-${h}`}
                style={{ flex: 1 }}
                placeholder={t('crm.imports.skip_column')}
                allowClear
                value={mapping[h] ?? undefined}
                onChange={(v) => handleMappingChange(h, v ?? '')}
                options={fieldOptions}
              />
            </div>
          ))}
          <Space style={{ marginTop: 16 }}>
            <Button onClick={() => setStep(1)}>{t('crm.imports.back')}</Button>
            <Button type="primary" onClick={handleSaveMapping} loading={loading}>
              {t('crm.imports.save_mapping')}
            </Button>
            <Button type="primary" danger onClick={handleExecute} loading={loading}>
              {t('crm.imports.run_import')}
            </Button>
          </Space>
        </Card>
      )}

      {step === 3 && result && (
        <Card data-testid="excel-import-result-card" title={t('crm.imports.result')}>
          <Space>
            <Text>
              {t('crm.imports.created')}{' '}
              <Text strong style={{ color: 'var(--ant-color-success)' }}>
                {result.created}
              </Text>
            </Text>
            <Text>
              {t('crm.imports.skipped')} <Text strong>{result.skipped}</Text>
            </Text>
            <Text>
              {t('crm.imports.errors')}{' '}
              <Text strong style={{ color: result.failed ? 'var(--ant-color-error)' : undefined }}>
                {result.failed}
              </Text>
            </Text>
          </Space>
          {result.errors.length > 0 && (
            <Table
              style={{ marginTop: 16 }}
              size="small"
              columns={[
                { title: t('crm.imports.col_row'), dataIndex: 'row', key: 'row', width: 80 },
                { title: t('crm.imports.col_error'), dataIndex: 'message', key: 'message' },
              ]}
              dataSource={result.errors.map((e, i) => ({ key: i, ...e }))}
              pagination={false}
            />
          )}
          <Button
            style={{ marginTop: 16 }}
            onClick={() => {
              setStep(0);
              setFile(null);
              setJobId(null);
              setJobUpdatedAt(null);
              setPreview(null);
              setResult(null);
            }}
          >
            {t('crm.imports.new_import')}
          </Button>
        </Card>
      )}

      <Divider />
      <Card title={t('crm.imports.export')} size="small">
        <Space>
          <Button icon={<DownloadOutlined />} onClick={() => exportEntities('contact')}>
            {t('crm.imports.export_contacts')}
          </Button>
          <Button icon={<DownloadOutlined />} onClick={() => exportEntities('company')}>
            {t('crm.imports.export_companies')}
          </Button>
          <Button icon={<DownloadOutlined />} onClick={() => exportEntities('deal')}>
            {t('crm.imports.export_deals')}
          </Button>
        </Space>
      </Card>
    </div>
  );
}
